import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button.jsx";
import { Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge.jsx";
import { ACTIONS } from "./CommandPalette.jsx";
import { useMenuConfig } from "@/hooks/useMenuConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/api/client";

export default function ActionsDropdown() {
  const [open, setOpen] = useState(false);
  const [actionCounts, setActionCounts] = useState({});
  const navigate = useNavigate();
  const { enabledActions, loading: configLoading } = useMenuConfig();
  const { user } = useAuth();

  useEffect(() => {
    loadActionCounts();
  }, []);

  const loadActionCounts = async () => {
    try {
      const res = await api.get("/action-counts");
      setActionCounts(res.data);
    } catch (err) {
      console.error("Fehler beim Laden der Action-Counts:", err);
    }
  };

  const handleAction = (action) => {
    navigate(action.path);
    setOpen(false);
  };

  // Total badge count
  const totalCount = Object.values(actionCounts).reduce((sum, count) => sum + count, 0);

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className="relative"
      >
        <Zap className="h-4 w-4 mr-2" />
        Aktionen
        {totalCount > 0 && (
          <Badge 
            variant="destructive" 
            className="ml-2 h-5 min-w-5 flex items-center justify-center px-1"
          >
            {totalCount}
          </Badge>
        )}
      </Button>

      {open && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={() => setOpen(false)}
          />
          
          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border z-[110] py-2">
            <div className="px-3 py-2 border-b">
              <p className="text-sm font-semibold text-gray-700">Aktionen</p>
            </div>
            
            <div className="py-1">
              {configLoading ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  Lade Menü...
                </div>
              ) : (
                enabledActions.map((configItem) => {
                  // Finde die entsprechende Action aus ACTIONS
                  const action = ACTIONS.find(a => a.id === configItem.id);
                  if (!action) return null;
                  
                  // Prüfe Rollen-Berechtigung
                  if (action.roles && user?.role && !action.roles.includes(user.role)) {
                    return null; // User hat keine Berechtigung für diese Action
                  }
                  
                  const Icon = action.icon;
                  const count = actionCounts[action.id];
                  
                  return (
                    <button
                      key={action.id}
                      onClick={() => handleAction(action)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                    >
                      <Icon className={`h-5 w-5 ${action.color}`} />
                      <span className="flex-1 text-sm font-medium">{action.label}</span>
                      {count > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {count}
                        </Badge>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            <div className="border-t px-3 py-2 mt-1">
              <p className="text-xs text-gray-500">
                Tipp: Drücke <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 border rounded">⌘K</kbd> für Schnellzugriff
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
